import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { imageFileMap } from "./markdownExportUtils";

interface PDFExportOptions {
  filename?: string;
  author?: string;
  title?: string;
  subject?: string;
}

interface ParsedMarkdownElement {
  type:
    | "heading"
    | "paragraph"
    | "table"
    | "list"
    | "pagebreak"
    | "italic"
    | "bold"
    | "image";
  content: string;
  level?: number; // for headings
  rows?: string[][]; // for tables
  items?: string[]; // for lists
  hasHeaders?: boolean; // for tables
  alt?: string; // for images
}

/**
 * Load image file and convert to base64
 */
async function loadImageFile(filename: string): Promise<string | null> {
  const imageFile = imageFileMap.get(filename);
  if (!imageFile) {
    console.warn(`Image file not found in map: ${filename}`);
    // Check if the image map has any entries
    console.log("Image map contents:", Array.from(imageFileMap.keys()));
    return null;
  }

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      console.log(
        `Successfully loaded image: ${filename}, data URL length: ${
          result?.length || 0
        }`
      );
      resolve(result);
    };
    reader.onerror = (error) => {
      console.error(`Failed to read image file: ${filename}`, error);
      resolve(null);
    };
    reader.readAsDataURL(imageFile);
  });
}

/**
 * Parse markdown content into structured elements
 */
function parseMarkdown(markdown: string): ParsedMarkdownElement[] {
  const elements: ParsedMarkdownElement[] = [];
  const lines = markdown.split("\n");

  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trim();

    // Check for pagebreak
    if (line === "\\pagebreak") {
      elements.push({ type: "pagebreak", content: "" });
      i++;
      continue;
    }

    // Check for images
    const imageMatch = line.match(/!\[([^\]]*)\]\(([^)]+)\)/);
    if (imageMatch) {
      elements.push({
        type: "image",
        content: imageMatch[2], // filename
        alt: imageMatch[1] || "",
      });
      i++;
      continue;
    }

    // Check for headings
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      elements.push({
        type: "heading",
        content: headingMatch[2],
        level: headingMatch[1].length,
      });
      i++;
      continue;
    }

    // Check for horizontal rule
    if (line === "---") {
      elements.push({
        type: "paragraph",
        content: "---",
      });
      i++;
      continue;
    }

    // Check for italic text (breadcrumbs)
    if (line.startsWith("*") && line.endsWith("*") && !line.startsWith("**")) {
      elements.push({
        type: "italic",
        content: line.slice(1, -1),
      });
      i++;
      continue;
    }

    // Check for bold text
    if (line.startsWith("**") && line.endsWith("**")) {
      elements.push({
        type: "bold",
        content: line.slice(2, -2),
      });
      i++;
      continue;
    }

    // Check for tables (both with and without headers)
    if (line.startsWith("|")) {
      const tableRows: string[][] = [];
      let hasHeader = false;

      // Check if next line is a separator (indicates header row)
      if (i + 1 < lines.length && lines[i + 1].trim().includes("---")) {
        hasHeader = true;
      }

      // Parse table starting from current line
      let j = i;
      while (j < lines.length && lines[j].trim().startsWith("|")) {
        const currentLine = lines[j].trim();

        // Skip separator lines (containing ---)
        if (currentLine.includes("---")) {
          j++;
          continue;
        }

        // Parse the row
        const row = currentLine
          .split("|")
          .map((cell) => cell.trim())
          .filter((cell, index, arr) => {
            // Filter out empty cells from the beginning and end
            return index !== 0 && index !== arr.length - 1;
          });

        if (row.length > 0) {
          tableRows.push(row);
        }
        j++;
      }

      if (tableRows.length > 0) {
        elements.push({
          type: "table",
          content: "",
          rows: tableRows,
          hasHeaders: hasHeader,
        });
      }

      i = j;
      continue;
    }

    // Check for list items
    if (line.startsWith("- ") || line.match(/^\d+\.\s/)) {
      const items: string[] = [];
      let j = i;

      while (
        j < lines.length &&
        (lines[j].trim().startsWith("- ") || lines[j].trim().match(/^\d+\.\s/))
      ) {
        const item = lines[j]
          .trim()
          .replace(/^-\s/, "")
          .replace(/^\d+\.\s/, "");
        items.push(item);
        j++;
      }

      elements.push({
        type: "list",
        content: "",
        items,
      });

      i = j;
      continue;
    }

    // Regular paragraph
    if (line.length > 0) {
      elements.push({
        type: "paragraph",
        content: line,
      });
    }

    i++;
  }

  return elements;
}

/**
 * Configure PDF with compact settings
 */
function configurePDF(pdf: jsPDF): void {
  // Set default font to Times (using built-in Times substitute)
  pdf.setFont("times");

  // Set academic paper margins (in mm) - typical LaTeX margins
  pdf.setProperties({
    title: "Exported Document",
    creator: "Markdown to PDF Exporter",
  });
}

/**
 * Calculate the natural width needed for a table
 */
function calculateTableNaturalWidth(
  pdf: jsPDF,
  rows: string[][],
  fontSize: number,
  cellPadding: { top: number; right: number; bottom: number; left: number }
): number {
  pdf.setFont("times", "normal");
  pdf.setFontSize(fontSize);

  // Calculate total horizontal padding per cell
  const horizontalPadding = cellPadding.left + cellPadding.right;
  const columnWidths: number[] = [];

  // Calculate max width needed for each column
  for (let col = 0; col < rows[0].length; col++) {
    let maxWidth = 0;

    // Check all rows including header
    for (let row = 0; row < rows.length; row++) {
      const text = rows[row][col] || "";
      const fontStyle = row === 0 ? "bold" : "normal";
      pdf.setFont("times", fontStyle);

      // Get text width and add padding
      const textWidth = pdf.getTextWidth(text) + horizontalPadding;
      maxWidth = Math.max(maxWidth, textWidth);
    }

    columnWidths.push(maxWidth);
  }

  // Calculate total width with inter-column spacing
  // autoTable adds small gaps between columns, approximately 1mm per gap
  const interColumnSpacing = (rows[0].length - 1) * 1;
  const totalWidth =
    columnWidths.reduce((sum, width) => sum + width, 0) + interColumnSpacing;

  // Add buffer for table borders and internal spacing
  // autoTable adds approximately 2-3mm of additional space for borders and internal calculations
  return totalWidth + 3;
}

/**
 * Add content to PDF based on parsed elements
 */
async function addContentToPDF(
  pdf: jsPDF,
  elements: ParsedMarkdownElement[]
): Promise<void> {
  const pageWidth = pdf.internal.pageSize.width;
  const pageHeight = pdf.internal.pageSize.height;

  // Reduced margins for more compact layout
  const leftMargin = 20; // Reduced from 25.4
  const rightMargin = 20; // Reduced from 25.4
  const topMargin = 20; // Reduced from 25.4
  const bottomMargin = 20; // Reduced from 25.4
  const contentWidth = pageWidth - leftMargin - rightMargin;

  let yPosition = topMargin;

  // Smaller base font size for compactness
  const baseFontSize = 10; // Reduced from 11

  for (let idx = 0; idx < elements.length; idx++) {
    const element = elements[idx];

    // Check if we need a new page
    if (yPosition > pageHeight - bottomMargin - 10) {
      pdf.addPage();
      yPosition = topMargin;
    }

    switch (element.type) {
      case "pagebreak":
        pdf.addPage();
        yPosition = topMargin;
        break;

      case "image":
        if (element.content) {
          const imageData = await loadImageFile(element.content);
          if (imageData) {
            try {
              // Calculate image dimensions
              const img = new Image();
              img.src = imageData;

              // Wait for image to load to get dimensions
              await new Promise<void>((resolve) => {
                img.onload = () => resolve();
                img.onerror = () => resolve();
              });

              if (img.width && img.height) {
                // Calculate scaled dimensions to fit page width
                const maxWidth = contentWidth; // Use full content width
                const aspectRatio = img.height / img.width;
                let imgWidth = img.width;
                let imgHeight = img.height;

                // Scale to fit page width if image is large
                // Consider "large" as images wider than 50% of content width
                if (imgWidth > contentWidth * 0.5) {
                  imgWidth = contentWidth;
                  imgHeight = imgWidth * aspectRatio;
                } else if (imgWidth > maxWidth) {
                  // Still scale down if somehow wider than page
                  imgWidth = maxWidth;
                  imgHeight = imgWidth * aspectRatio;
                }

                // Check if image fits on current page
                if (yPosition + imgHeight > pageHeight - bottomMargin) {
                  pdf.addPage();
                  yPosition = topMargin;
                }

                // Position image based on size
                let xPosition;
                if (imgWidth >= contentWidth * 0.5) {
                  // Large images: align with margins
                  xPosition = leftMargin;
                } else {
                  // Small images: center
                  xPosition = leftMargin + (contentWidth - imgWidth) / 2;
                }

                // Add image to PDF
                pdf.addImage(
                  imageData,
                  "JPEG",
                  xPosition,
                  yPosition,
                  imgWidth,
                  imgHeight
                );
                yPosition += imgHeight + 5; // Reduced spacing

                // Add alt text as caption if available
                if (element.alt) {
                  pdf.setFontSize(9); // Smaller caption
                  pdf.setFont("times", "italic");
                  pdf.setTextColor(60, 60, 60);

                  const caption = `Figure: ${element.alt}`;
                  const captionLines = pdf.splitTextToSize(
                    caption,
                    contentWidth
                  );

                  /* eslint-disable no-loop-func */
                  captionLines.forEach((line: string, index: number) => {
                    const lineWidth = pdf.getTextWidth(line);
                    const captionX =
                      leftMargin + (contentWidth - lineWidth) / 2;
                    pdf.text(line, captionX, yPosition + index * 3.5);
                  });
                  /* eslint-enable no-loop-func */

                  yPosition += captionLines.length * 3.5 + 3;

                  pdf.setFont("times", "normal");
                  pdf.setTextColor(0, 0, 0);
                  pdf.setFontSize(baseFontSize);
                }
              }
            } catch (error) {
              console.error(`Error adding image ${element.content}:`, error);
              // Add placeholder text for failed image
              pdf.setFontSize(9);
              pdf.setFont("times", "italic");
              pdf.text(`[Image: ${element.content}]`, leftMargin, yPosition);
              yPosition += 4;
              pdf.setFont("times", "normal");
              pdf.setFontSize(baseFontSize);
            }
          } else {
            // Add placeholder for missing image
            pdf.setFontSize(9);
            pdf.setFont("times", "italic");
            pdf.text(
              `[Image not found: ${element.content}]`,
              leftMargin,
              yPosition
            );
            yPosition += 4;
            pdf.setFont("times", "normal");
            pdf.setFontSize(baseFontSize);
          }
        }
        break;

      case "heading":
        // Compact heading sizes
        let fontSize = baseFontSize;
        let spaceBefore = 0;
        let spaceAfter = 0;

        switch (element.level) {
          case 1: // Section
            fontSize = 12; // Reduced from 14
            spaceBefore = 8; // Reduced from 12
            spaceAfter = 4; // Reduced from 6
            break;
          case 2: // Subsection
            fontSize = 11; // Reduced from 12
            spaceBefore = 6; // Reduced from 10
            spaceAfter = 3; // Reduced from 4
            break;
          case 3: // Subsubsection
            fontSize = baseFontSize;
            spaceBefore = 4; // Reduced from 8
            spaceAfter = 2; // Reduced from 4
            break;
          default:
            fontSize = baseFontSize;
            spaceBefore = 3; // Reduced from 6
            spaceAfter = 2; // Reduced from 3
        }

        yPosition += spaceBefore;

        pdf.setFontSize(fontSize);
        pdf.setFont("times", "bold");

        const headingLines = pdf.splitTextToSize(element.content, contentWidth);
        pdf.text(headingLines, leftMargin, yPosition);
        yPosition += headingLines.length * (fontSize * 0.35) + spaceAfter;

        pdf.setFont("times", "normal");
        break;

      case "italic":
        pdf.setFontSize(baseFontSize);
        pdf.setFont("times", "italic");
        pdf.setTextColor(60, 60, 60);

        const italicLines = pdf.splitTextToSize(element.content, contentWidth);
        pdf.text(italicLines, leftMargin, yPosition);
        yPosition += italicLines.length * 3.5 + 2; // Reduced spacing

        pdf.setFont("times", "normal");
        pdf.setTextColor(0, 0, 0);
        break;

      case "bold":
        pdf.setFontSize(baseFontSize);
        pdf.setFont("times", "bold");

        const boldLines = pdf.splitTextToSize(element.content, contentWidth);
        pdf.text(boldLines, leftMargin, yPosition);
        yPosition += boldLines.length * 4 + 2; // Reduced spacing

        pdf.setFont("times", "normal");
        break;

      case "paragraph":
        pdf.setFontSize(baseFontSize);
        pdf.setFont("times", "normal");

        if (element.content === "---") {
          // Draw horizontal line (thinner for academic style)
          pdf.setDrawColor(0, 0, 0);
          pdf.setLineWidth(0.25);
          pdf.line(leftMargin, yPosition, pageWidth - rightMargin, yPosition);
          yPosition += 6; // Reduced from 10
        } else {
          const lines = pdf.splitTextToSize(element.content, contentWidth);
          pdf.text(lines, leftMargin, yPosition);
          // Tighter line spacing
          yPosition += lines.length * 4 + 3; // Reduced from 5 + 6
        }
        break;

      case "list":
        pdf.setFontSize(baseFontSize);
        pdf.setFont("times", "normal");

        if (element.items) {
          for (
            let itemIndex = 0;
            itemIndex < element.items.length;
            itemIndex++
          ) {
            const item = element.items[itemIndex];

            if (yPosition > pageHeight - bottomMargin - 8) {
              pdf.addPage();
              yPosition = topMargin;
            }

            const bullet = "•";
            const itemText = bullet + " " + item;
            const itemLines = pdf.splitTextToSize(itemText, contentWidth - 10);

            pdf.text(itemLines, leftMargin + 5, yPosition);
            yPosition += itemLines.length * 4 + 2; // Reduced spacing
          }
        }
        break;

      case "table":
        if (element.rows && element.rows.length > 0) {
          // Create a mutable reference for the position
          const positionRef = { value: yPosition };

          // Calculate table position with minimal spacing
          const tableStartY = yPosition + 4; // Reduced from 8

          // Define cell padding to match autoTable settings
          const tableFontSize = 9; // Increased back to 9 for better readability
          // Minimal padding for compact layout
          const cellPadding = { top: 0.5, right: 2, bottom: 0.5, left: 2 };
          const headerCellPadding = { top: 1, right: 2, bottom: 1, left: 2 };

          // Use the hasHeaders flag from the parsed element
          const hasHeaders = element.hasHeaders || false;

          // Split rows into head and body based on the hasHeaders flag
          const headRows = hasHeaders ? [element.rows[0]] : [];
          const bodyRows = hasHeaders ? element.rows.slice(1) : element.rows;

          // Calculate the natural width needed for the table
          const naturalWidth = calculateTableNaturalWidth(
            pdf,
            element.rows,
            tableFontSize,
            cellPadding
          );

          // Determine if table should be centered or full width
          const isNarrowTable = naturalWidth < contentWidth * 0.75;

          // For narrow tables, we need to account for autoTable's actual rendering
          // Add extra buffer to ensure content doesn't overflow
          const tableWidthBuffer = isNarrowTable
            ? naturalWidth * 1.1
            : contentWidth;
          const actualTableWidth = Math.min(tableWidthBuffer, contentWidth);

          const tableLeftMargin = isNarrowTable
            ? leftMargin + (contentWidth - actualTableWidth) / 2
            : leftMargin;

          // Store table data for drawing lines after table is complete
          const tableData = {
            actualX: tableLeftMargin,
            actualWidth: actualTableWidth,
            topLineDrawn: false,
            headerY: 0,
            hasHeaders: hasHeaders,
          };

          // Create callback functions outside the loop to avoid closure issues
          const didDrawCellCallback = (data: any) => {
            // Store the actual table x position and width from first and last cells
            if (data.row.index === 0) {
              if (data.column.index === 0) {
                tableData.actualX = data.cell.x;
              }
              if (data.column.index === data.table.columns.length - 1) {
                tableData.actualWidth =
                  data.cell.x + data.cell.width - tableData.actualX;
                if (data.row.section === "head") {
                  tableData.headerY = data.cell.y + data.cell.height;
                }
              }
            }
          };

          const didDrawPageCallback = (data: any) => {
            // Draw top line
            pdf.setLineWidth(0.25);
            pdf.setDrawColor(0, 0, 0);
            pdf.line(
              tableData.actualX,
              tableStartY - 1,
              tableData.actualX + tableData.actualWidth,
              tableStartY - 1
            );

            // Draw line after header (only if table has headers)
            if (tableData.hasHeaders && tableData.headerY > 0) {
              pdf.setLineWidth(0.125);
              pdf.line(
                tableData.actualX,
                tableData.headerY,
                tableData.actualX + tableData.actualWidth,
                tableData.headerY
              );
            }

            // Draw bottom line
            if (data.cursor) {
              pdf.setLineWidth(0.25);
              pdf.line(
                tableData.actualX,
                data.cursor.y,
                tableData.actualX + tableData.actualWidth,
                data.cursor.y
              );
              positionRef.value = data.cursor.y + 5; // Update the reference
            }
          };

          // Compact table styling
          autoTable(pdf, {
            head: headRows,
            body: bodyRows,
            startY: tableStartY,
            margin: { left: tableLeftMargin, right: rightMargin },
            tableWidth: isNarrowTable ? actualTableWidth : contentWidth,
            theme: "plain",
            styles: {
              font: "times",
              fontSize: tableFontSize,
              cellPadding: cellPadding,
              lineColor: [0, 0, 0],
              lineWidth: 0,
              valign: "middle",
              halign: "left",
              textColor: [0, 0, 0],
              fillColor: false,
              overflow: "linebreak",
            },
            headStyles: {
              fillColor: false,
              textColor: [0, 0, 0],
              fontStyle: "bold",
              lineWidth: 0,
              cellPadding: headerCellPadding,
            },
            bodyStyles: {
              fillColor: false,
              lineWidth: 0,
            },
            alternateRowStyles: {
              fillColor: false,
            },
            columnStyles: {},
            tableLineColor: [0, 0, 0],
            tableLineWidth: 0,
            showHead: hasHeaders ? "firstPage" : "never",
            // Use the callbacks defined outside the loop
            didDrawCell: didDrawCellCallback,
            didDrawPage: didDrawPageCallback,
          });

          // Update yPosition from the reference
          yPosition = positionRef.value;

          // Fallback if didDrawPage didn't set yPosition
          if (yPosition <= tableStartY) {
            yPosition = tableStartY + 20;
          }
        }
        break;
    }
  }
}

/**
 * Export markdown content to PDF with compact formatting
 */
export async function exportMarkdownToPDF(
  markdownContent: string,
  options: PDFExportOptions = {}
): Promise<void> {
  const {
    filename = "export.pdf",
    author = "",
    title = "Exported Document",
    subject = "Markdown Export",
  } = options;

  // Create new PDF document
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  // Configure PDF
  configurePDF(pdf);

  // Set document properties
  pdf.setProperties({
    title,
    author,
    subject,
    creator: "Markdown to PDF Exporter",
  });

  // Parse markdown
  const elements = parseMarkdown(markdownContent);

  // Add content to PDF (now async to handle images)
  await addContentToPDF(pdf, elements);

  // Add page numbers in compact style
  const totalPages = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.setFont("times", "normal");
    pdf.setFontSize(9); // Smaller page numbers
    pdf.setTextColor(0, 0, 0);

    // Center page number at bottom
    pdf.text(
      `${i}`,
      pdf.internal.pageSize.width / 2,
      pdf.internal.pageSize.height - 10, // Closer to bottom
      { align: "center" }
    );
  }

  // Save the PDF
  pdf.save(filename);
}

/**
 * Generate PDF from markdown with progress callback
 */
export async function generatePDFWithProgress(
  markdownContent: string,
  options: PDFExportOptions = {},
  onProgress?: (progress: number) => void
): Promise<void> {
  try {
    onProgress?.(10);

    // Simulate progress for PDF generation
    await new Promise((resolve) => setTimeout(resolve, 100));
    onProgress?.(50);

    await exportMarkdownToPDF(markdownContent, options);

    onProgress?.(100);
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw error;
  }
}
